import { apiFetch, authHeaders } from '@/shared/api/client';
import {
  CASAS_DOLAR,
  type CasaDolar,
  type VistaMoneda,
} from '@/shared/lib/tipoCambio';

/**
 * Reportes API wrappers. Each report hits the same endpoint shape:
 *   GET /api/reportes/:reportId?format=json  → preview JSON
 *   GET /api/reportes/:reportId?format=xlsx  → download XLSX (opens
 *       natively in Excel; numeric cells stay numeric)
 *
 * Preview returns a structured `{ header: string[], rows: Record[] }`
 * so the UI can render a generic table for every report without
 * bespoke column definitions.
 */

export type ReportId =
  | 'retenciones'
  | 'iva-compras'
  | 'iva-ventas'
  | 'comisiones'
  | 'ventas-efectivo'
  | 'pagos-efectivo'
  | 'cc-proveedores'
  | 'cc-ventas'
  | 'despachos'
  | 'cierres-de-caja'
  | 'movimientos-historico'
  | 'costo-contable'
  | 'productos-vendidos'
  | 'garantias'
  // Bespoke reports: no `/api/reportes/:id` handler behind them. They own
  // their own body + data source (`/api/facturacion/facturas` for
  // `facturacion`) and the Reportes shell never previews or exports them.
  | 'facturacion'
  | 'estado-financiero';

export type ReportFilters = {
  /** `dd/mm/yyyy`. Used by date-range reports. */
  desde?: string;
  hasta?: string;
  /** `mm/yyyy`. Used by monthly reports. */
  mes?: string;
  /** Vendedor name. Comisiones-only — narrows rows + totals to one
   *  vendedor. Empty / undefined = all vendedores. */
  vendedor?: string;
};

export type ReportTotal = { label: string; value: string };

export type ReportPreviewResponse = {
  header: string[];
  rows: Array<Record<string, string | number>>;
  totalRowCount: number;
  totals: ReportTotal[];
  /** Comisiones-only — full vendedor roster for the month, regardless of
   *  the active vendedor filter. Populates the vendedor dropdown. */
  vendedores?: string[];
  /** Comisiones-only (AR) — cobros MercadoPago a los que Tesorería todavía
   *  no les cargó los cargos. Respeta el filtro de vendedor. */
  cobrosMpSinCargos?: Array<{
    movimiento: number;
    fecha: string | null;
    monto: number;
    venta: string;
    cliente: string;
    vendedora: string;
  }>;
};

export async function getReportPreview(
  reportId: ReportId,
  filters: ReportFilters,
  /** Buscar/ordenar en el server — para los reportes cuya vista previa llega recortada. */
  tabla?: { q?: string; orden?: string; dir?: 'asc' | 'desc' },
): Promise<ReportPreviewResponse> {
  const query: Record<string, string> = { format: 'json' };
  if (filters.desde) query.desde = filters.desde;
  if (filters.hasta) query.hasta = filters.hasta;
  if (filters.mes) query.mes = filters.mes;
  if (filters.vendedor) query.vendedor = filters.vendedor;
  if (tabla?.q) query.q = tabla.q;
  if (tabla?.orden) {
    query.orden = tabla.orden;
    query.dir = tabla.dir ?? 'asc';
  }
  return apiFetch<ReportPreviewResponse>(`/api/reportes/${reportId}`, { query });
}

/**
 * Download the XLSX. Uses a manual fetch so we can attach the auth
 * token, then drops a blob into an `<a download>` element. The
 * browser handles save-dialog.
 */
export async function downloadReportXlsx(
  reportId: ReportId,
  filters: ReportFilters,
  suggestedFilename: string,
): Promise<void> {
  const query = new URLSearchParams({ format: 'xlsx' });
  if (filters.desde) query.set('desde', filters.desde);
  if (filters.hasta) query.set('hasta', filters.hasta);
  if (filters.mes) query.set('mes', filters.mes);
  if (filters.vendedor) query.set('vendedor', filters.vendedor);
  const res = await fetch(`/api/reportes/${reportId}?${query.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Download failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedFilename.endsWith('.xlsx')
    ? suggestedFilename
    : `${suggestedFilename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ---------------------------------------------------------------------------
// Cierres de Caja report — home-banking style browser (year + caja filter,
// list of cierres, click for movimientos preview, per-cierre XLSX export).
// Multi-country: `currency` widens to include MXN for MX cierres.
// ---------------------------------------------------------------------------

export type CierreSummary = {
  id: number;
  sharepointId: number | null;
  idCierreCaja: string;
  fecha: string;
  caja: string;
  saldoCerrado: number;
  currency: 'ARS' | 'MXN' | 'USD';
  usuario: string | null;
  descripcion: string | null;
  observaciones: string | null;
  movimientosCount: number;
  ingresos: number;
  egresos: number;
};

export type CierreMovimiento = {
  /** `sharepoint_id` del movimiento — lo necesita la reclasificación de
   *  cuenta contable. No sale en el .xlsx (no está en `header`). */
  Id: string | number;
  Fecha: string;
  Tipo: string;
  Categoria: string;
  'Cuenta Contable': string;
  Caja: string;
  Descripcion: string;
  Cliente: string;
  Observaciones: string;
  Usuario: string;
  Status: string;
  Moneda: string;
  Monto: string;
};

export async function listCierresAnios(): Promise<number[]> {
  const res = await apiFetch<{ anios: number[] }>('/api/reportes/cierres-de-caja/anios');
  return res.anios;
}

export async function listCierresDeCaja(anio: number, caja: string): Promise<CierreSummary[]> {
  const res = await apiFetch<{ cierres: CierreSummary[] }>(
    '/api/reportes/cierres-de-caja',
    { query: { anio: String(anio), caja } },
  );
  return res.cierres;
}

export async function previewCierreMovimientos(
  idCierreCaja: string,
): Promise<{ header: string[]; rows: CierreMovimiento[]; totals: ReportTotal[] }> {
  return apiFetch(
    `/api/reportes/cierres-de-caja/${encodeURIComponent(idCierreCaja)}/movimientos`,
  );
}

export async function downloadCierreMovimientosXlsx(
  idCierreCaja: string,
  suggestedFilename: string,
): Promise<void> {
  const res = await fetch(
    `/api/reportes/cierres-de-caja/${encodeURIComponent(idCierreCaja)}/movimientos?format=xlsx`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Download failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedFilename.endsWith('.xlsx')
    ? suggestedFilename
    : `${suggestedFilename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ---------------------------------------------------------------------------
// Estado Financiero — month-by-month statement grouped by EF Ítem / SubÍtem,
// plus the per-cell drill-down. Server: routes/estadoFinancieroReport.ts.
// ---------------------------------------------------------------------------

/** Columnas de la tabla: una por mes, o una por fecha con movimientos. */
export type EfGranularidad = 'mensual' | 'diario';

/**
 * `local` — todo en la moneda del país (las filas en dólares, convertidas).
 * `usd`   — sólo los movimientos cargados en dólares, con el importe tal
 *           como se ingresó. Los dos totales NO reconcilian: son
 *           poblaciones distintas, no la misma plata en otra moneda.
 */
export type EfVista = VistaMoneda;

/**
 * Casa de cambio con la que se convierte. El vocabulario (casas + etiquetas)
 * vive en `@/shared/lib/tipoCambio` desde que Tesorería usa los mismos
 * controles; acá quedan los alias con los que ya se lo nombra en el reporte.
 */
export const EF_CASAS = CASAS_DOLAR;
export type EfCasa = CasaDolar;

export type EfPeriodo = {
  /** `MM/YYYY`, inclusive on both ends. */
  desde: string;
  hasta: string;
  granularidad: EfGranularidad;
  vista: EfVista;
  /** Con qué serie se convierte. Cambia TODOS los números del tablero. */
  casa: EfCasa;
};

/**
 * Los parámetros que definen la conversión. Van en TODAS las llamadas del
 * reporte: si un detalle convirtiera con otra casa, no sumaría la celda que
 * lo abrió.
 */
function qConversion(p: { vista: EfVista; casa: EfCasa }) {
  return { casa: p.casa, ...(p.vista === 'usd' ? { moneda: 'USD' } : {}) };
}

export type EfRow = {
  kind: 'item' | 'subitem' | 'computed';
  label: string;
  /** Drill-down key; null on Ítem rows, computed lines and Sin clasificar. */
  subitemId: number | null;
  seccion: 'ingresos' | 'directos' | 'indirectos' | 'otros' | 'computed';
  /** Signed amounts keyed by `MM-YYYY`. Egresos are negative. */
  porMes: Record<string, number>;
  total: number;
};

export type EfCaucionDia = {
  fecha: string;
  /** Running G/P at the START of the day — what the model prints. */
  gpAcum: number;
  /** Interest accrued through the previous day. */
  intAnterior: number;
  base: number;
  tasa: number;
  intDia: number;
};

export type EfResponse = {
  /** Claves de columna en orden: `MM-YYYY` (mensual) o `YYYY-MM-DD` (diario). */
  meses: string[];
  granularidad: EfGranularidad;
  vista: EfVista;
  /** La casa con la que el backend convirtió estos números. */
  casa: EfCasa;
  rows: EfRow[];
  flujoOperativoPorMes: Record<string, number>;
  kpis: { ingresos: number; egresos: number; gpOperativo: number };
  sinClasificar: { movimientos: number; monto: number };
  /** Filas sin rueda para su fecha: quedan fuera del estado. */
  sinCotizacion: { movimientos: number; usd: number };
  /** Panel "Flujo por mes": una fila por período + Total + proyectados. */
  flujoPorMes: { rows: FlujoMesRow[]; total: FlujoMesRow; proyectadas: FlujoMesRow[] };
  /** Gráfico de consumo, por clave de columna + `total`. */
  consumo: Record<string, ConsumoPeriodo>;
  /** Alertas por mes (vacío en granularidad diaria). */
  alertas: Record<string, Alerta[]>;
  alertaDesvioPct: number;
  /** Filas en dólares convertidas con la cotización estimada del mes. */
  /** Movimientos que todavía no pasaron por Cierre de Caja. */
  sinConfirmar: { movimientos: number; monto: number; meses: string[] };
  /** `null` en la vista en dólares: la serie de tasas es en pesos. */
  caucion: {
    pct: number;
    /** Accrual start — fixed, not tied to the period filter. */
    desde: string;
    porMes: Record<string, number>;
    detallePorMes: Record<string, EfCaucionDia[]>;
    tasasCargadas: number;
    /** Fecha ISO de la última tasa cargada; `null` sin serie. */
    ultimaTasa: string | null;
    /** Períodos devengados arrastrando esa última tasa. */
    extrapolado: { meses: number; monto: number };
  } | null;
  moneda: string;
  desde: string;
  hasta: string;
};

export type FlujoMesRow = {
  mes: string;
  ingresos: number;
  egresos: number;
  gpOperativo: number;
  /** Fracción (0,363 = 36,3%); `null` cuando no hay denominador. */
  comisionesSobreVentas: number | null;
  costoDirectoSobreIngresos: number | null;
  costoIndirectoSobreIngresos: number | null;
  costoTotalSobreIngresos: number | null;
  caucion: number;
};

/** Clave del período "Total" dentro de `EfResponse.consumo`. */
export const CONSUMO_TOTAL = 'total';

/**
 * ponytail: interruptor de la caución en el tablero — el cliente no la
 * necesita por ahora (pedido 31/08/2026). Apaga la columna de Flujo por mes,
 * la fila del Estado Financiero, las dos salidas del Excel y el aviso de
 * serie vencida. El backend la sigue calculando: para volver a mostrarla
 * alcanza con poner `true` acá, no hay nada más que tocar.
 */
export const MOSTRAR_CAUCION = false;

export type ConsumoPeriodo = {
  barras: Array<{ label: string; valor: number }>;
  margenBruto: number;
  flujoNeto: number;
  /** Ingreso operativo del período — denominador del "% del Flujo Op.". */
  base: number;
};

export type Alerta = {
  tipo: 'desvio' | 'sin_egresos' | 'falta_categoria';
  mensaje: string;
  /** Sólo en `desvio`: fracción con signo. */
  desvio?: number;
  categoria?: string;
};

export type EvolucionAgrupar = 'categoria' | 'cuenta' | 'subitem';

export type EvolucionRow = { label: string; porMes: Record<string, number>; total: number };

export type EvolucionResponse = {
  meses: string[];
  granularidad: EfGranularidad;
  vista: EfVista;
  agrupar: EvolucionAgrupar;
  rows: EvolucionRow[];
  totalPorMes: Record<string, number>;
  total: number;
  proyeccion: { meses: string[]; porLabel: Record<string, Record<string, number>> };
  moneda: string;
};

export async function getEvolucion(
  periodo: EfPeriodo,
  agrupar: EvolucionAgrupar,
): Promise<EvolucionResponse> {
  return apiFetch<EvolucionResponse>('/api/reportes/estado-financiero/evolucion', {
    query: {
      desde: periodo.desde,
      hasta: periodo.hasta,
      periodo: periodo.granularidad,
      agrupar,
      ...qConversion(periodo),
    },
  });
}

export type FlujoDetalleBloque = {
  id: 'ingresos' | 'directos' | 'indirectos' | 'otros';
  label: string;
  rows: Array<{ label: string; monto: number }>;
  total: number;
};

export async function getFlujoDetalle(args: {
  /** Clave de la celda: `MM-YYYY` o `YYYY-MM-DD`. */
  mes: string;
  agrupar: EvolucionAgrupar;
  vista: EfVista;
  casa: EfCasa;
}): Promise<{
  bloques: FlujoDetalleBloque[];
  mes: string;
  agrupar: EvolucionAgrupar;
  moneda: string;
}> {
  return apiFetch('/api/reportes/estado-financiero/flujo-detalle', {
    query: {
      mes: args.mes,
      agrupar: args.agrupar,
      ...qConversion(args),
    },
  });
}

export async function getEvolucionDetalle(args: {
  /** Clave de la celda: `MM-YYYY` o `YYYY-MM-DD`. */
  mes: string;
  /** La etiqueta de la fila, tal como la muestra la tabla. */
  label: string;
  agrupar: EvolucionAgrupar;
  vista: EfVista;
  casa: EfCasa;
}): Promise<{ mes: string; etiqueta: string; moneda: string; rows: EfDetalleRow[]; total: number }> {
  return apiFetch('/api/reportes/estado-financiero/evolucion/detalle', {
    query: {
      mes: args.mes,
      label: args.label,
      agrupar: args.agrupar,
      ...qConversion(args),
    },
  });
}

export type EfDetalleRow = {
  fecha: string;
  categoria: string;
  cuentaContable: string;
  descripcion: string;
  cliente: string;
  usuario: string;
  monto: number;
};

export async function getEstadoFinanciero(periodo: EfPeriodo): Promise<EfResponse> {
  return apiFetch<EfResponse>('/api/reportes/estado-financiero', {
    query: {
      desde: periodo.desde,
      hasta: periodo.hasta,
      periodo: periodo.granularidad,
      ...qConversion(periodo),
    },
  });
}

export async function getEstadoFinancieroDetalle(args: {
  /** Deben coincidir con las de la tabla; si no, el detalle no suma la celda. */
  vista: EfVista;
  casa: EfCasa;
  /** null = the "Sin clasificar" bucket. */
  subitemId: number | null;
  /** La clave de la celda: `MM-YYYY` o `YYYY-MM-DD`. */
  mes: string;
}): Promise<{ mes: string; moneda: string; rows: EfDetalleRow[]; total: number }> {
  return apiFetch('/api/reportes/estado-financiero/detalle', {
    query: {
      mes: args.mes,
      ...(args.subitemId === null ? {} : { subitemId: String(args.subitemId) }),
      ...qConversion(args),
    },
  });
}

// ---------------------------------------------------------------------------
// Reimportar un .xlsx para reclasificar
// ---------------------------------------------------------------------------
//
// Mismo contrato para los dos reportes que lo ofrecen: Cierres de Caja (un
// cierre) e Histórico de Movimientos (el libro entero). Cambia el endpoint y
// el encabezado que se exige; la respuesta es idéntica.

export type ReclasImportCambio = {
  id: number;
  descripcion: string;
  categoriaActual: string;
  cuentaActual: string;
  categoriaNueva: string;
  cuentaNueva: string;
  /** Presente = la fila cambió pero no se puede aplicar, y por qué. */
  problema?: string;
  /** `par_invalido` / `incompleto` se arreglan en pantalla; el resto no. */
  problemaTipo?:
    | 'par_invalido'
    | 'incompleto'
    | 'anulado'
    | 'estructural'
    | 'automatismo'
    | 'desalineado';
  /** Pares reales del catálogo que tocan lo que el usuario escribió. */
  opciones?: Array<{ cuenta: string; categoria: string }>;
};

export type ReclasImportResumen = {
  filasLeidas: number;
  conCambios: number;
  conProblema: number;
  sinCambios: number;
  /** Filas cuyo `Id` no existe en el universo importado: se ignoran. */
  noEncontrados: number;
  filasVacias: number;
};

export type ReclasImportResponse =
  | { outcome: 'ok'; cambios: ReclasImportCambio[]; resumen: ReclasImportResumen }
  | {
      outcome: 'layout_invalido';
      message: string;
      esperado: string[];
      recibido: string[];
    }
  | { outcome: 'archivo_invalido'; message: string };

/**
 * Sube el Excel corregido y devuelve QUÉ cambiaría. No escribe nada: aplicar
 * es un segundo paso, fila por fila, contra el endpoint de reimputación de
 * Tesorería (que valida el par, respeta permisos y deja rastro).
 */
export async function importarCierreMovimientos(
  idCierreCaja: string,
  file: File,
): Promise<ReclasImportResponse> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(
    `/api/reportes/cierres-de-caja/${encodeURIComponent(idCierreCaja)}/movimientos/importar`,
    { method: 'POST', headers: authHeaders(), body: form },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Import failed: ${res.status} ${text}`);
  }
  return (await res.json()) as ReclasImportResponse;
}

/**
 * Igual que el anterior pero sobre el Histórico de Movimientos: el universo es
 * todo el libro del país, así que una fila se casa por `Id` sin importar en qué
 * cierre (o en ninguno) esté.
 */
export async function importarMovimientosHistorico(
  file: File,
): Promise<ReclasImportResponse> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/reportes/movimientos-historico/importar', {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Import failed: ${res.status} ${text}`);
  }
  return (await res.json()) as ReclasImportResponse;
}
